import React, { useState } from 'react';
import { formatDate, toLocalISO } from '../../utils/dateFormat';
import { useVatMode } from '../../hooks/useVatMode';
import { getOrderNumber } from '../../utils/orderTitle';
import { hireLineCost } from '../../utils/hireCost';
import { FIELD_LABEL_CLASS } from '../../constants/inputs';
import HireBulkDialogFrame from './HireBulkDialogFrame';

/**
 * The latest current last-hire-day among `lines`, and the line that holds it
 * (`null` when no line has a `hire_to`). The new date must be AFTER this one:
 * `POST /api/hires/orders/:orderId/extend-all` refuses the WHOLE order with a
 * 400 naming the first line whose `hire_to >= new_hire_to`, so the dialog
 * refuses first, and says which line sets the bar.
 */
export function latestHireEnd(lines) {
  return (lines || []).reduce(
    (best, l) => (l.hire_to && (!best || l.hire_to > best.hire_to) ? l : best),
    null
  );
}

/** The day after an ISO date — the earliest date the picker offers. */
function dayAfter(iso) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return toLocalISO(d);
}

/**
 * The inline refusal for a picked date, or `''` when it is valid. Exported so
 * the rule is testable on its own: the date must be after EVERY listed line's
 * current last day (strictly — the same day is not an extension) and on or
 * after every line's start.
 */
export function extendAllDateError(lines, newDate) {
  if (!newDate) return 'Choose the new last day of hire.';
  const blocking = (lines || []).find((l) => l.hire_to && newDate <= l.hire_to);
  if (blocking) {
    const latest = latestHireEnd(lines);
    return `Pick a date after ${formatDate(latest.hire_to)} — "${latest.description}" already runs to then.`;
  }
  const early = (lines || []).find((l) => l.hire_from && newDate < l.hire_from);
  if (early) return `The date is before "${early.description}" starts (${formatDate(early.hire_from)}).`;
  return '';
}

/**
 * EXTEND ALL… — one new last day of hire for every line of an order still on
 * hire, ONE confirm, all-or-nothing (owner ruling 2026-09-28: *"add Extend
 * all"*, the Off hire all pattern). `lines` are hire-list rows already
 * narrowed by the caller to this order's lines that are not off hired.
 *
 * 💷 Extending RE-PRICES each line at its own rate (the server's
 * `extendLineInTx`, the same writer as single-line Extend). So each line shows
 * its current total and what it becomes at the picked date — the dialog IS
 * the money confirmation; nothing is charged until Confirm.
 *
 * `onConfirm({ new_hire_to, item_ids })` — the caller sends it (and handles a
 * keep/remove adjustment question); `error` is its last server refusal.
 */
function ExtendAllDialog({ order, lines, onConfirm, onCancel, loading = false, error = '' }) {
  const { formatMoney } = useVatMode();
  const [newDate, setNewDate] = useState('');
  const [dateError, setDateError] = useState('');

  if (!order || !lines) return null;

  const latest = latestHireEnd(lines);
  const count = lines.length;
  const noun = count === 1 ? 'line' : 'lines';

  const handleSubmit = (e) => {
    e.preventDefault();
    const refusal = extendAllDateError(lines, newDate);
    if (refusal) {
      setDateError(refusal);
      return;
    }
    onConfirm({ new_hire_to: newDate, item_ids: lines.map((l) => l.item_id) });
  };

  const dateValid = !!newDate && !extendAllDateError(lines, newDate);

  return (
    <HireBulkDialogFrame
      testIdPrefix="extend-all"
      title="Extend all"
      subtitle={`${count} hire ${noun} still on hire on ${getOrderNumber(order)}`}
      tone="blue"
      onSubmit={handleSubmit}
      onCancel={onCancel}
      loading={loading}
      error={error}
      footnote="All or nothing: if any line cannot be extended, none are."
      confirmLabel={`Extend ${count} ${noun}`}
      busyLabel="Extending…"
    >
      <div>
        <label htmlFor="extend-all-date" className={FIELD_LABEL_CLASS}>New last day of hire</label>
        <input
          id="extend-all-date"
          type="date"
          value={newDate}
          min={latest ? dayAfter(latest.hire_to) : undefined}
          onChange={(e) => { setNewDate(e.target.value); setDateError(''); }}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
          data-testid="extend-all-date"
          autoFocus
        />
        <p className="text-xs mt-1 text-gray-500 dark:text-gray-400" data-testid="extend-all-rule">
          {latest
            ? `Applies to every line below. Must be after ${formatDate(latest.hire_to)}, the latest current last day.`
            : 'Applies to every line below.'}
        </p>
        {dateError && (
          <p className="text-xs mt-1 text-red-600 dark:text-red-400" data-testid="extend-all-date-error">{dateError}</p>
        )}
      </div>

      <ul className="divide-y divide-gray-200 dark:divide-gray-700 border rounded-lg border-gray-200 dark:border-gray-700">
        {lines.map((line) => {
          const id = line.item_id;
          const repriced = dateValid ? hireLineCost({ ...line, hire_to: newDate }) : null;
          return (
            <li key={id} className="px-3 py-3 space-y-1" data-testid={`extend-all-line-${id}`}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-0 truncate">{line.description}</span>
                <span className="text-sm tabular-nums text-gray-700 dark:text-gray-300 shrink-0">
                  {formatMoney(line.line_total)}
                  {repriced != null && (
                    <span className="text-blue-700 dark:text-blue-300" data-testid={`extend-all-new-price-${id}`}> → {formatMoney(repriced)}</span>
                  )}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatDate(line.hire_from)} to {formatDate(line.hire_to)}
                {dateValid && <> → {formatDate(newDate)}</>}
              </p>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Each line is re-priced at its own rate. A line with a price adjustment asks whether to keep it.
      </p>
    </HireBulkDialogFrame>
  );
}

export default ExtendAllDialog;
